/**
 * @fileoverview Componente responsável por exibir ações disponíveis para cada mensagem.
 */

import React, { useState, useContext } from "react";

import { makeStyles } from "@material-ui/core/styles";
import { Popover, Grow } from "@material-ui/core";
import { Reply, Pencil, Forward, Trash2, RotateCcw } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import EditMessageModal from "../EditMessageModal";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission from "../../hooks/usePermission";
import toastError from "../../errors/toastError";

const REACTION_EMOJIS = ["❤️", "😂", "😮", "😢", "👍", "🙏"];
const EDIT_TIME_LIMIT_MINUTES = 10;

const useStyles = makeStyles((theme) => ({
	messageMenuPaper: {
		borderRadius: 14,
		marginTop: 6,
		overflow: "visible",
		background: theme.palette.type === "dark" ? "#1c1f22" : "#ffffff",
		border: theme.palette.type === "dark"
			? "1px solid rgba(255, 255, 255, 0.06)"
			: "1px solid rgba(15, 23, 42, 0.06)",
		boxShadow: theme.palette.type === "dark"
			? "0 20px 40px -12px rgba(0, 0, 0, 0.5), 0 6px 14px -4px rgba(0, 0, 0, 0.3)"
			: "0 20px 40px -12px rgba(15, 23, 42, 0.22), 0 6px 14px -4px rgba(15, 23, 42, 0.10)",
	},
	messageMenu: {
		display: "flex",
		flexDirection: "column",
		padding: "6px 4px",
		minWidth: 232,
	},
	reactionBar: {
		display: "flex",
		justifyContent: "space-between",
		gap: 2,
		padding: "8px 8px 6px",
	},
	reactionBtn: {
		fontSize: 20,
		cursor: "pointer",
		lineHeight: 1,
		padding: "4px 6px",
		borderRadius: 8,
		background: "transparent",
		border: "none",
		transition: "transform 140ms ease, background-color 140ms ease",
		"&:hover": {
			transform: "scale(1.2)",
			backgroundColor: theme.palette.type === "dark"
				? "rgba(255, 255, 255, 0.05)"
				: "rgba(15, 23, 42, 0.04)",
		},
		"&:focus-visible": {
			outline: `2px solid ${theme.palette.type === "dark" ? "#5B7CFF" : "#3D5CFD"}`,
			outlineOffset: 1,
		},
	},
	reactionBtnActive: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(91, 124, 255, 0.18)" : "#EEF2FF",
	},
	messageMenuSection: {
		display: "flex",
		flexDirection: "column",
		gap: 2,
	},
	messageMenuEyebrow: {
		fontSize: 10,
		fontWeight: 700,
		letterSpacing: "0.14em",
		textTransform: "uppercase",
		padding: "8px 12px 4px",
		color: theme.palette.type === "dark" ? "#6b7380" : "#94A3B8",
	},
	messageMenuItem: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		padding: "8px 10px",
		borderRadius: 8,
		border: "none",
		background: "transparent",
		width: "100%",
		cursor: "pointer",
		textAlign: "left",
		fontFamily: "inherit",
		fontSize: 13,
		fontWeight: 500,
		color: theme.palette.type === "dark" ? "#e6e8eb" : "#1a1a1a",
		transition: "background-color 140ms ease",
		animation: "$messageMenuItemIn 200ms cubic-bezier(0.16, 1, 0.3, 1) both",
		"&:hover": {
			backgroundColor: theme.palette.type === "dark"
				? "rgba(255, 255, 255, 0.05)"
				: "rgba(15, 23, 42, 0.04)",
		},
		"&:focus-visible": {
			outline: `2px solid ${theme.palette.type === "dark" ? "#5B7CFF" : "#3D5CFD"}`,
			outlineOffset: 1,
		},
		"&:nth-child(1)": { animationDelay: "0ms" },
		"&:nth-child(2)": { animationDelay: "35ms" },
		"&:nth-child(3)": { animationDelay: "70ms" },
		"&:nth-child(4)": { animationDelay: "105ms" },
	},
	messageMenuItemDanger: {
		color: theme.palette.type === "dark" ? "#fca5a5" : "#dc2626",
		"&:hover": {
			backgroundColor: theme.palette.type === "dark"
				? "rgba(220, 38, 38, 0.12)"
				: "rgba(220, 38, 38, 0.06)",
		},
	},
	messageMenuIconBox: {
		width: 28,
		height: 28,
		flex: "0 0 28px",
		borderRadius: 8,
		display: "grid",
		placeItems: "center",
	},
	messageMenuIconBoxCobalt: {
		background: theme.palette.type === "dark" ? "rgba(91, 124, 255, 0.16)" : "#EEF2FF",
		color: theme.palette.type === "dark" ? "#93a6ff" : "#3D5CFD",
	},
	messageMenuIconBoxSlate: {
		background: theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.16)" : "#F1F5F9",
		color: theme.palette.type === "dark" ? "#cbd5e1" : "#475569",
	},
	messageMenuIconBoxRed: {
		background: theme.palette.type === "dark" ? "rgba(248, 113, 113, 0.14)" : "#FEE2E2",
		color: theme.palette.type === "dark" ? "#fca5a5" : "#dc2626",
	},
	messageMenuLabel: {
		flex: 1,
		lineHeight: 1.2,
	},
	messageMenuDivider: {
		height: 1,
		margin: "6px 8px",
		background: theme.palette.type === "dark"
			? "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)"
			: "linear-gradient(90deg, transparent, #E2E8F0, transparent)",
	},
	"@keyframes messageMenuItemIn": {
		"0%": { opacity: 0, transform: "translateY(4px)" },
		"100%": { opacity: 1, transform: "translateY(0)" },
	},
}));

const MessageOptionsMenu = ({ message, ticket, menuOpen, handleClose, anchorEl, onEnableForwardMode }) => {
	const classes = useStyles();
	const { setReplyingMessage } = useContext(ReplyMessageContext);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);

	// Provider vem do ticket (contexto da conversa). `message.ticket` só é
	// populado em eventos de socket recém-enviados; no reload via API ele é
	// undefined — por isso preferimos a prop `ticket` como fonte primária.
	const provider =
		ticket?.whatsapp?.provider || message?.ticket?.whatsapp?.provider;

	// Mensagem de outro autor (ou sem autor gravado): editar e apagar pedem
	// messages.manage_others (hoje, aberto a todos; o servidor confere).
	const { user } = useContext(AuthContext);
	const { can } = usePermission();
	const ownMessage = message.senderUserId != null && Number(message.senderUserId) === Number(user?.id);
	const managesMessage = ownMessage || can("messages.manage_others", true);

	// fromMe + provider waha/livechat + dentro de 10 min + não deletada/editada + sem mídia.
	const canEdit = () => {
		if (!message.fromMe || !managesMessage) return false;
		if (provider !== "waha" && provider !== "livechat") return false;
		if (message.isDeleted) return false;
		if (message.isEdited) return false;
		if (message.mediaUrl || message.mediaType) return false;
		const diffMinutes =
			(Date.now() - new Date(message.createdAt).getTime()) / 60000;
		return diffMinutes <= EDIT_TIME_LIMIT_MINUTES;
	};

	const canDelete = message.fromMe && provider !== "meta-api" && managesMessage;

	// Reenvio só faz sentido pra mensagens nossas que falharam (ack=-1) com
	// PK local-<uuid>. Restringido a Meta API por enquanto — WAHA seria só
	// adicionar provider !== "meta-api" check do outro lado.
	const canRetry =
		message.fromMe &&
		message.ack === -1 &&
		typeof message.id === "string" &&
		message.id.startsWith("local-") &&
		provider === "meta-api";

	const [retrying, setRetrying] = useState(false);

	const handleRetry = async () => {
		if (retrying) return;
		setRetrying(true);
		try {
			await api.post(`/messages/${message.id}/retry`);
			handleClose();
		} catch (err) {
			toastError(err);
		} finally {
			setRetrying(false);
		}
	};

	const handleDeleteMessage = async () => {
		try {
			await api.delete(`/messages/${message.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleReplyMessage = () => {
		setReplyingMessage(message);
		handleClose();
	};

	const handleForwardMessage = () => {
		onEnableForwardMode();
		handleClose();
	};

	const handleOpenEditModal = () => {
		setEditModalOpen(true);
		handleClose();
	};

	const handleCloseEditModal = () => {
		setEditModalOpen(false);
	};

	const handleOpenConfirmationModal = () => {
		setConfirmationOpen(true);
		handleClose();
	};

	const myReaction = Array.isArray(message?.reactions)
		? (message.reactions.find((r) => r.fromMe)?.text || null)
		: null;

	const handleReact = async (emoji) => {
		handleClose();
		const reaction = emoji === myReaction ? "" : emoji;
		try {
			await api.post(`/messages/${message.id}/react`, {
				ticketId: message.ticketId,
				emoji: reaction,
			});
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<React.Fragment>
			<ConfirmationModal
				title={i18n.t("messageOptionsMenu.confirmationModal.title")}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteMessage}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>

			<EditMessageModal
				open={editModalOpen}
				onClose={handleCloseEditModal}
				message={message}
			/>

			<Popover
				anchorEl={anchorEl}
				open={menuOpen}
				onClose={handleClose}
				onClick={(e) => e.stopPropagation()}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				TransitionComponent={Grow}
				transitionDuration={{ enter: 200, exit: 140 }}
				PaperProps={{ className: classes.messageMenuPaper }}
			>
				<div className={classes.messageMenu} role="menu" data-role="message-menu">
					<div className={classes.reactionBar}>
						{REACTION_EMOJIS.map((emoji) => (
							<button
								key={emoji}
								type="button"
								aria-label={`Reagir com ${emoji}`}
								aria-pressed={emoji === myReaction}
								className={`${classes.reactionBtn}${emoji === myReaction ? ` ${classes.reactionBtnActive}` : ""}`}
								onClick={() => handleReact(emoji)}
							>
								{emoji}
							</button>
						))}
					</div>

					<div className={classes.messageMenuDivider} />

					<div className={classes.messageMenuSection}>
						<div className={classes.messageMenuEyebrow}>Ação</div>
						{canRetry && (
							<button
								type="button"
								role="menuitem"
								className={classes.messageMenuItem}
								onClick={handleRetry}
								disabled={retrying}
							>
								<span className={`${classes.messageMenuIconBox} ${classes.messageMenuIconBoxCobalt}`}>
									<RotateCcw size={16} strokeWidth={1.8} />
								</span>
								<span className={classes.messageMenuLabel}>
									{retrying
										? i18n.t("messageOptionsMenu.retrying")
										: i18n.t("messageOptionsMenu.retry")}
								</span>
							</button>
						)}
						<button
							type="button"
							role="menuitem"
							className={classes.messageMenuItem}
							onClick={handleReplyMessage}
						>
							<span className={`${classes.messageMenuIconBox} ${classes.messageMenuIconBoxCobalt}`}>
								<Reply size={16} strokeWidth={1.8} />
							</span>
							<span className={classes.messageMenuLabel}>
								{i18n.t("messageOptionsMenu.reply")}
							</span>
						</button>

						{canEdit() && (
							<button
								type="button"
								role="menuitem"
								className={classes.messageMenuItem}
								onClick={handleOpenEditModal}
							>
								<span className={`${classes.messageMenuIconBox} ${classes.messageMenuIconBoxSlate}`}>
									<Pencil size={16} strokeWidth={1.8} />
								</span>
								<span className={classes.messageMenuLabel}>
									{i18n.t("messageOptionsMenu.edit")}
								</span>
							</button>
						)}

						<button
							type="button"
							role="menuitem"
							className={classes.messageMenuItem}
							onClick={handleForwardMessage}
						>
							<span className={`${classes.messageMenuIconBox} ${classes.messageMenuIconBoxSlate}`}>
								<Forward size={16} strokeWidth={1.8} />
							</span>
							<span className={classes.messageMenuLabel}>
								{i18n.t("messageOptionsMenu.forward")}
							</span>
						</button>
					</div>

					{canDelete && (
						<>
							<div className={classes.messageMenuDivider} />
							<div className={classes.messageMenuSection}>
								<button
									type="button"
									role="menuitem"
									className={`${classes.messageMenuItem} ${classes.messageMenuItemDanger}`}
									onClick={handleOpenConfirmationModal}
								>
									<span className={`${classes.messageMenuIconBox} ${classes.messageMenuIconBoxRed}`}>
										<Trash2 size={16} strokeWidth={1.8} />
									</span>
									<span className={classes.messageMenuLabel}>
										{i18n.t("messageOptionsMenu.delete")}
									</span>
								</button>
							</div>
						</>
					)}
				</div>
			</Popover>
		</React.Fragment>
	);
};

export default MessageOptionsMenu;
